/**
 * Residences Block — Smooth tab switching + AJAX location filtering.
 *
 * Both tab panels (locations & apartments) are pre-rendered in the DOM.
 * Switching tabs uses CSS opacity/visibility transitions (no AJAX needed).
 * Location filtering within the apartments tab uses AJAX.
 */

import Swiper from 'swiper';
import {Navigation} from 'swiper/modules';

function pad(n) {
    return String(n).padStart(2, '0');
}

function updateCounter(swiper, currentEl, totalEl) {
    if (currentEl) {
        const pageIndex = swiper.snapIndex !== undefined ? swiper.snapIndex : swiper.activeIndex;
        currentEl.textContent = pad(pageIndex + 1);
    }
    if (totalEl) {
        const totalPages = swiper.snapGrid ? swiper.snapGrid.length : 1;
        totalEl.textContent = pad(Math.max(totalPages, 1));
    }
}

function createSwiper(swiperEl, prevBtn, nextBtn, currentEl, totalEl) {
    if (!swiperEl) return null;
    if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
    }

    return new Swiper(swiperEl, {
        modules: [Navigation],
        slidesPerView: 1.05,
        spaceBetween: 16,
        grabCursor: true,
        watchOverflow: true,
        breakpoints: {
            701: {slidesPerView: 2, spaceBetween: 24},
            1121: {slidesPerView: 3.05, spaceBetween: 32},
            1730: {slidesPerView: 3.42, spaceBetween: 32},
        },
        navigation: {
            prevEl: prevBtn || null,
            nextEl: nextBtn || null,
        },
        on: {
            init(s) {
                updateCounter(s, currentEl, totalEl);
            },
            slideChange(s) {
                updateCounter(s, currentEl, totalEl);
            },
            resize(s) {
                updateCounter(s, currentEl, totalEl);
            },
        },
    });
}

function initResidencesBlock(block) {
    const restUrl = block.dataset.restUrl;
    const nonce = block.dataset.restNonce;
    const perPage = parseInt(block.dataset.postsPerPage, 10) || 8;
    const excludeId = parseInt(block.dataset.excludeId, 10) || 0;
    // Base heading WITHOUT location suffix — always "Explore our residences"
    const headingRaw = block.querySelector('[data-residences-heading]')?.textContent?.trim() || '';
    const baseHeading = headingRaw.replace(/\s+in\s+.+$/i, '');

    const tabsContainer = block.querySelector('[data-residences-tabs]');
    const filtersBar = block.querySelector('[data-residences-filters]');
    const headingEl = block.querySelector('[data-residences-heading]');
    const prevBtn = block.querySelector('[data-swiper-prev]');
    const nextBtn = block.querySelector('[data-swiper-next]');
    const currentEl = block.querySelector('[data-swiper-current]');
    const totalEl = block.querySelector('[data-swiper-total]');

    // Panels
    const locationsPanel = block.querySelector('[data-panel="locations"]');
    const apartmentsPanel = block.querySelector('[data-panel="apartments"]');
    const locationsSwiperEl = locationsPanel?.querySelector('.swiper');
    const apartmentsSwiperEl = apartmentsPanel?.querySelector('.swiper');
    const apartmentsCards = apartmentsPanel?.querySelector('[data-residences-cards]');

    if (!locationsPanel || !apartmentsPanel) return;

    const cache = {};
    let activeTab = block.dataset.defaultTab || 'locations';
    let activeLocationId = '';
    let locationSwiper = null;
    let apartmentSwiper = null;

    // ── Panel switching (CSS animation, no AJAX) ─────────────────────────────

    function switchPanel(tab) {
        const panels = block.querySelectorAll('[data-panel]');
        panels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.panel === tab);
        });

        // Re-init the active panel's swiper (needs to recalculate after becoming visible)
        setTimeout(() => {
            if ('locations' === tab) {
                locationSwiper = createSwiper(locationsSwiperEl, prevBtn, nextBtn, currentEl, totalEl);
            } else {
                apartmentSwiper = createSwiper(apartmentsSwiperEl, prevBtn, nextBtn, currentEl, totalEl);
            }
        }, 50); // small delay for CSS transition to start
    }

    function activateTab(tab) {
        activeTab = tab;

        // Update tab buttons.
        if (tabsContainer) {
            tabsContainer.querySelectorAll('[data-tab]').forEach((btn) => {
                const isActive = btn.dataset.tab === tab;
                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
        }

        // Show/hide filter bar.
        if (filtersBar) {
            if ('apartments' === tab) {
                filtersBar.removeAttribute('hidden');

                // Activate the PHP-rendered .is-active filter (or first as fallback) if none active.
                if (!activeLocationId) {
                    const activeFilter =
                        filtersBar.querySelector('[data-location].is-active') ||
                        filtersBar.querySelector('[data-location]');
                    if (activeFilter) {
                        activeLocationId = activeFilter.dataset.location;
                        const firstName = activeFilter.textContent.trim();
                        filtersBar.querySelectorAll('[data-location]').forEach((btn) => {
                            btn.classList.toggle('is-active', btn.dataset.location === activeLocationId);
                        });
                        if (headingEl && firstName) {
                            headingEl.textContent = 'Explore our residences in ' + firstName;
                        }
                    }
                }
            } else {
                filtersBar.setAttribute('hidden', '');
            }
        }

        // Reset heading for locations tab.
        if ('locations' === tab && headingEl) {
            headingEl.textContent = baseHeading;
        }

        switchPanel(tab);
    }

    // ── Location filter (AJAX) ───────────────────────────────────────────────

    async function activateFilter(locationId, locationName) {
        activeLocationId = locationId;

        // Update filter buttons.
        if (filtersBar) {
            filtersBar.querySelectorAll('[data-location]').forEach((btn) => {
                btn.classList.toggle('is-active', btn.dataset.location === locationId);
            });
        }

        // Update heading.
        if (headingEl) {
            if (locationName) {
                headingEl.textContent = 'Explore our residences in ' + locationName;
            } else {
                headingEl.textContent = baseHeading;
            }
        }

        // Fetch filtered cards.
        const cacheKey = 'apartments:' + locationId;
        if (apartmentsCards) {
            apartmentsCards.classList.add('is-loading');
        }

        try {
            let data;
            if (cache[cacheKey]) {
                data = cache[cacheKey];
            } else {
                const url = new URL(restUrl);
                url.searchParams.set('per_page', perPage);
                if (locationId) {
                    url.searchParams.set('location', locationId);
                }
                if (excludeId) {
                    url.searchParams.set('exclude', excludeId);
                }
                const response = await fetch(url.toString(), {
                    headers: {'X-WP-Nonce': nonce},
                });
                if (!response.ok) throw new Error('REST ' + response.status);
                data = await response.json();
                cache[cacheKey] = data;
            }

            if (apartmentsCards) {
                apartmentsCards.innerHTML = data.html || '';
            }
        } catch (err) {
            console.error('[Residences]', err);
        } finally {
            if (apartmentsCards) {
                apartmentsCards.classList.remove('is-loading');
            }
            apartmentSwiper = createSwiper(apartmentsSwiperEl, prevBtn, nextBtn, currentEl, totalEl);
        }
    }

    // ── Events ────────────────────────────────────────────────────────────────

    if (tabsContainer) {
        tabsContainer.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-tab]');
            if (!btn) return;
            const tab = btn.dataset.tab;
            if (tab !== activeTab) {
                activateTab(tab);
            }
        });
    }

    if (filtersBar) {
        filtersBar.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-location]');
            if (!btn) return;
            const locationId = btn.dataset.location;
            const locationName = btn.textContent.trim();
            if (locationId !== activeLocationId) {
                activateFilter(locationId, locationName);
            }
        });
    }

    // ── Bootstrap ─────────────────────────────────────────────────────────────

    // Init the active panel's swiper.
    if ('locations' === activeTab) {
        locationSwiper = createSwiper(locationsSwiperEl, prevBtn, nextBtn, currentEl, totalEl);
    } else {
        apartmentSwiper = createSwiper(apartmentsSwiperEl, prevBtn, nextBtn, currentEl, totalEl);

        // When apartments is the default tab, ensure filter bar is visible
        // and the active filter (PHP-rendered .is-active, or the first as fallback)
        // is reflected in JS state. PHP already rendered the correct heading.
        if (filtersBar && !activeLocationId) {
            filtersBar.removeAttribute('hidden');
            const activeFilter =
                filtersBar.querySelector('[data-location].is-active') ||
                filtersBar.querySelector('[data-location]');
            if (activeFilter) {
                activeLocationId = activeFilter.dataset.location;
                filtersBar.querySelectorAll('[data-location]').forEach((btn) => {
                    btn.classList.toggle('is-active', btn.dataset.location === activeLocationId);
                });
            }
        }
    }
}

export const ResidencesBlock = {
    init() {
        document.querySelectorAll('[data-residences-block]').forEach(initResidencesBlock);
    },
};
